import { useEffect, useRef, useState } from "react";
import { ChevronsRight, Loader2 } from "lucide-react";

type SlideToConfirmProps = {
  label: string;
  onConfirm: () => void | Promise<void>;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
};

const THUMB_SIZE = 56;
const TRACK_HEIGHT = 64;
const CONFIRM_THRESHOLD = 0.8;

export function SlideToConfirm({
  label,
  onConfirm,
  disabled = false,
  loading = false,
  className = "",
}: SlideToConfirmProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const pointerStartXRef = useRef<number | null>(null);
  const startXRef = useRef(0);

  function maxX(): number {
    const track = trackRef.current;
    if (!track) return 0;
    return Math.max(0, track.offsetWidth - THUMB_SIZE);
  }

  function progress(): number {
    const max = maxX();
    return max === 0 ? 0 : dragX / max;
  }

  function handlePointerDown(clientX: number) {
    if (disabled || loading || confirmed) return;
    pointerStartXRef.current = clientX;
    startXRef.current = dragX;
    setDragging(true);
  }

  function handlePointerMove(clientX: number) {
    if (!dragging || pointerStartXRef.current === null) return;
    const delta = clientX - pointerStartXRef.current;
    const next = Math.max(0, Math.min(maxX(), startXRef.current + delta));
    setDragX(next);
  }

  function handlePointerEnd() {
    if (!dragging) return;
    setDragging(false);
    pointerStartXRef.current = null;
    const max = maxX();
    const ratio = max === 0 ? 0 : dragX / max;
    if (ratio >= CONFIRM_THRESHOLD) {
      setDragX(max);
      setConfirmed(true);
      Promise.resolve(onConfirm()).finally(() => {});
    } else {
      setDragX(0);
    }
  }

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: PointerEvent) => handlePointerMove(e.clientX);
    const onUp = () => handlePointerEnd();
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  });

  useEffect(() => {
    if (!loading && confirmed) {
      const t = setTimeout(() => {
        setConfirmed(false);
        setDragX(0);
      }, 400);
      return () => clearTimeout(t);
    }
  }, [loading, confirmed]);

  const p = progress();
  const isBusy = loading || confirmed;
  const trackColor = disabled
    ? "bg-muted"
    : p > 0.5
    ? "bg-primary"
    : "bg-primary/20";

  return (
    <div
      ref={trackRef}
      className={`relative w-full select-none overflow-hidden rounded-full ${trackColor} ${className}`}
      style={{ height: TRACK_HEIGHT, touchAction: "none" }}
    >
      <div
        className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm font-semibold text-primary-foreground"
        style={{ opacity: Math.max(0.3, 1 - p) }}
      >
        {label}
      </div>
      <div
        role="button"
        tabIndex={0}
        aria-label={label}
        aria-disabled={disabled}
        className="absolute top-1 left-1 flex items-center justify-center rounded-full bg-white text-primary shadow-md"
        style={{
          width: THUMB_SIZE - 8,
          height: THUMB_SIZE - 8,
          transform: `translateX(${dragX}px)`,
          transition: dragging ? "none" : "transform 0.2s ease-out",
          cursor: disabled ? "not-allowed" : isBusy ? "default" : "grab",
        }}
        onPointerDown={(e) => {
          (e.target as Element).setPointerCapture?.(e.pointerId);
          handlePointerDown(e.clientX);
        }}
      >
        {isBusy ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <ChevronsRight className="h-6 w-6" />
        )}
      </div>
    </div>
  );
}
